/**
 * webStorage.js — IndexedDB wrapper for the web demo build
 *
 * Mirrors the desktop app's filesystem layout for projects and user templates,
 * plus a generic cache store for parsed CSV lookups.
 *
 *   projects        keyPath: safeName        (the project_id; older entries: the sanitized name)
 *   userTemplates   keyPath: fileName        (mirrors ~/Documents/LiTeCAB/user_templates/*.json)
 *   cache           keyPath: key             (parsed TRACI/cost CSV, with version field)
 *
 * Only used by ipcWebShim.js. The Electron build never touches this module.
 */

import { openDB } from 'idb';

const DB_NAME = 'litecab-web';
const DB_VERSION = 2;

let dbPromise = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('projects', { keyPath: 'safeName' });
          db.createObjectStore('userTemplates', { keyPath: 'fileName' });
          db.createObjectStore('cache', { keyPath: 'key' });
        }
        if (oldVersion < 2) {
          // AI Template Builder drafts
          db.createObjectStore('aiDrafts', { keyPath: 'draftId' });
        }
      },
    });
  }
  return dbPromise;
}

// -- Projects ---------------------------------------------------------------

export async function putProject(safeName, projectData) {
  const db = await getDb();
  await db.put('projects', {
    safeName,
    data: projectData,
    lastSaved: new Date().toISOString(),
  });
}

export async function getProject(safeName) {
  const db = await getDb();
  const entry = await db.get('projects', safeName);
  return entry?.data || null;
}

export async function listProjects() {
  const db = await getDb();
  const all = await db.getAll('projects');
  return all.map(({ safeName, data, lastSaved }) => ({
    fileName: `${safeName}.json`,
    safeName,
    name: data?.project_info?.name || data?.project_name || safeName,
    description: data?.project_info?.description || '',
    chemistry: data?.project_info?.chemistry || 'Unknown',
    filePath: `idb://projects/${safeName}`,
    lastSaved,
    processCount: data?.processes ? Object.keys(data.processes).length : 0,
  }));
}

export async function deleteProject(safeName) {
  const db = await getDb();
  await db.delete('projects', safeName);
}

// -- User templates ---------------------------------------------------------

export async function putUserTemplate(fileName, templateData) {
  const db = await getDb();
  await db.put('userTemplates', {
    fileName,
    data: templateData,
    lastSaved: new Date().toISOString(),
  });
}

export async function getUserTemplate(fileName) {
  const db = await getDb();
  const entry = await db.get('userTemplates', fileName);
  return entry?.data || null;
}

export async function listUserTemplates() {
  const db = await getDb();
  const keys = await db.getAllKeys('userTemplates');
  return keys;
}

export async function deleteUserTemplate(fileName) {
  const db = await getDb();
  await db.delete('userTemplates', fileName);
}

// -- AI Builder drafts ------------------------------------------------------

export async function putAiDraft(draftId, templateData) {
  const db = await getDb();
  await db.put('aiDrafts', {
    draftId,
    data: templateData,
    savedAt: new Date().toISOString(),
    name: templateData?.project_name || draftId,
    processCount: templateData?.processes ? Object.keys(templateData.processes).length : 0,
  });
}

export async function listAiDrafts() {
  const db = await getDb();
  const all = await db.getAll('aiDrafts');
  return all.sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
}

export async function getAiDraft(draftId) {
  const db = await getDb();
  const entry = await db.get('aiDrafts', draftId);
  return entry?.data || null;
}

export async function deleteAiDraft(draftId) {
  const db = await getDb();
  await db.delete('aiDrafts', draftId);
}

// -- Generic cache ----------------------------------------------------------

export async function cacheGet(key) {
  const db = await getDb();
  const entry = await db.get('cache', key);
  return entry || null;
}

export async function cachePut(key, payload, version) {
  const db = await getDb();
  await db.put('cache', {
    key,
    version: version || 1,
    payload,
    cachedAt: new Date().toISOString(),
  });
}
